/**
 * Node Configuration Panel
 *
 * Provides configuration UI for workflow nodes
 */
import { useState } from 'react'
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import Button from '@/components/common/Button'
import Card from '@/components/common/Card'

interface NodeConfigPanelProps {
  nodeType: string
  nodeData: Record<string, any>
  onSave: (data: Record<string, any>) => void
  onClose: () => void
}

export function NodeConfigPanel({
  nodeType,
  nodeData,
  onSave,
  onClose,
}: NodeConfigPanelProps) {
  const [data, setData] = useState<Record<string, any>>(nodeData || {})

  const handleSave = () => {
    onSave(data)
    onClose()
  }

  const renderConfig = () => {
    switch (nodeType) {
      case 'conditional':
        return <ConditionalConfig data={data} onChange={setData} />
      case 'loop':
        return <LoopConfig data={data} onChange={setData} />
      case 'tryCatch':
        return <TryCatchConfig data={data} onChange={setData} />
      case 'retry':
        return <RetryConfig data={data} onChange={setData} />
      case 'llm':
        return <LLMConfig data={data} onChange={setData} />
      case 'tool':
        return <ToolConfig data={data} onChange={setData} />
      case 'agent':
        return <AgentConfig data={data} onChange={setData} />
      case 'humanInput':
        return <HumanInputConfig data={data} onChange={setData} />
      default:
        return <DefaultConfig />
    }
  }

  return (
    <div className="fixed inset-y-0 right-0 w-96 bg-white shadow-2xl z-50 overflow-y-auto">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">
            Configure {nodeType} Node
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Config Content */}
        <div className="space-y-4 mb-6">
          {renderConfig()}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button onClick={handleSave} variant="primary" className="flex-1">
            Save
          </Button>
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
        </div>
      </div>
    </div>
  )
}

// Conditional Node Configuration
function ConditionalConfig({
  data,
  onChange,
}: {
  data: Record<string, any>
  onChange: (data: Record<string, any>) => void
}) {
  const conditionTypes = [
    { value: 'equals', label: 'Equals' },
    { value: 'notEquals', label: 'Not Equals' },
    { value: 'greaterThan', label: 'Greater Than' },
    { value: 'lessThan', label: 'Less Than' },
    { value: 'contains', label: 'Contains' },
    { value: 'regex', label: 'Regex Match' },
    { value: 'exists', label: 'Exists' },
    { value: 'isEmpty', label: 'Is Empty' },
  ]

  const conditions = data.conditions || [{ type: 'equals', field: '', value: '' }]

  const addCondition = () => {
    onChange({
      ...data,
      conditions: [...conditions, { type: 'equals', field: '', value: '' }],
    })
  }

  const removeCondition = (index: number) => {
    onChange({
      ...data,
      conditions: conditions.filter((_: any, i: number) => i !== index),
    })
  }

  const updateCondition = (index: number, updates: Partial<any>) => {
    const newConditions = [...conditions]
    newConditions[index] = { ...newConditions[index], ...updates }
    onChange({ ...data, conditions: newConditions })
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Logic Operator
        </label>
        <select
          value={data.operator || 'AND'}
          onChange={(e) => onChange({ ...data, operator: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="AND">AND (all conditions must be true)</option>
          <option value="OR">OR (any condition must be true)</option>
        </select>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Conditions
          </label>
          <button
            onClick={addCondition}
            className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
          >
            <PlusIcon className="w-4 h-4" />
            Add Condition
          </button>
        </div>

        <div className="space-y-3">
          {conditions.map((condition: any, index: number) => (
            <Card key={index} className="p-3">
              <div className="space-y-2">
                <div className="flex gap-2">
                  <select
                    value={condition.type}
                    onChange={(e) => updateCondition(index, { type: e.target.value })}
                    className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                  >
                    {conditionTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  {conditions.length > 1 && (
                    <button
                      onClick={() => removeCondition(index)}
                      className="text-red-600 hover:text-red-600"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <input
                  type="text"
                  placeholder="Field path (e.g., output.result)"
                  value={condition.field}
                  onChange={(e) => updateCondition(index, { field: e.target.value })}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                />

                {!['exists', 'isEmpty'].includes(condition.type) && (
                  <input
                    type="text"
                    placeholder="Value to compare"
                    value={condition.value}
                    onChange={(e) => updateCondition(index, { value: e.target.value })}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                  />
                )}
              </div>
            </Card>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          True Output Label
        </label>
        <input
          type="text"
          value={data.trueLabel || 'True'}
          onChange={(e) => onChange({ ...data, trueLabel: e.target.value })}
          placeholder="Label for true branch"
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          False Output Label
        </label>
        <input
          type="text"
          value={data.falseLabel || 'False'}
          onChange={(e) => onChange({ ...data, falseLabel: e.target.value })}
          placeholder="Label for false branch"
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
        />
      </div>
    </div>
  )
}

// Loop Node Configuration
function LoopConfig({
  data,
  onChange,
}: {
  data: Record<string, any>
  onChange: (data: Record<string, any>) => void
}) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Loop Type
        </label>
        <select
          value={data.loopType || 'foreach'}
          onChange={(e) => onChange({ ...data, loopType: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="foreach">For Each (iterate over array)</option>
          <option value="while">While (condition-based)</option>
          <option value="count">Count (fixed iterations)</option>
        </select>
      </div>

      {data.loopType === 'foreach' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Array Path
          </label>
          <input
            type="text"
            value={data.arrayPath || ''}
            onChange={(e) => onChange({ ...data, arrayPath: e.target.value })}
            placeholder="e.g., output.items"
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
          <p className="text-xs text-gray-500 mt-1">
            Path to array in workflow context
          </p>
        </div>
      )}

      {data.loopType === 'while' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Condition
          </label>
          <input
            type="text"
            value={data.condition || ''}
            onChange={(e) => onChange({ ...data, condition: e.target.value })}
            placeholder="e.g., counter < 10"
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>
      )}

      {data.loopType === 'count' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Iterations
          </label>
          <input
            type="number"
            value={data.iterations || 1}
            onChange={(e) => onChange({ ...data, iterations: parseInt(e.target.value) })}
            min="1"
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Max Iterations (safety limit)
        </label>
        <input
          type="number"
          value={data.maxIterations || 100}
          onChange={(e) => onChange({ ...data, maxIterations: parseInt(e.target.value) })}
          min="1"
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Item Variable Name
        </label>
        <input
          type="text"
          value={data.itemVar || 'item'}
          onChange={(e) => onChange({ ...data, itemVar: e.target.value })}
          placeholder="e.g., item, element"
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
        />
        <p className="text-xs text-gray-500 mt-1">
          Variable name for current iteration value
        </p>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="parallel"
          checked={data.parallel || false}
          onChange={(e) => onChange({ ...data, parallel: e.target.checked })}
          className="w-4 h-4 text-primary-600 border-gray-300 rounded"
        />
        <label htmlFor="parallel" className="ml-2 text-sm text-gray-700">
          Run iterations in parallel (when possible)
        </label>
      </div>
    </div>
  )
}

// Try/Catch Node Configuration
function TryCatchConfig({
  data,
  onChange,
}: {
  data: Record<string, any>
  onChange: (data: Record<string, any>) => void
}) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Error Handling Strategy
        </label>
        <select
          value={data.strategy || 'catch'}
          onChange={(e) => onChange({ ...data, strategy: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
        >
          <option value="catch">Catch and Continue</option>
          <option value="catchAndTransform">Catch and Transform Error</option>
          <option value="catchAndFallback">Catch with Fallback Value</option>
        </select>
      </div>

      {data.strategy === 'catchAndFallback' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Fallback Value (JSON)
          </label>
          <textarea
            value={data.fallbackValue || '{}'}
            onChange={(e) => onChange({ ...data, fallbackValue: e.target.value })}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"
          />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Error Types to Catch
        </label>
        <div className="space-y-2">
          {['all', 'timeout', 'validation', 'llm', 'tool'].map((type) => (
            <label key={type} className="flex items-center">
              <input
                type="checkbox"
                checked={(data.errorTypes || ['all']).includes(type)}
                onChange={(e) => {
                  const types = data.errorTypes || ['all']
                  const newTypes = e.target.checked
                    ? [...types, type]
                    : types.filter((t: string) => t !== type)
                  onChange({ ...data, errorTypes: newTypes })
                }}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700 capitalize">{type}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="logError"
          checked={data.logError !== false}
          onChange={(e) => onChange({ ...data, logError: e.target.checked })}
          className="w-4 h-4 text-primary-600 border-gray-300 rounded"
        />
        <label htmlFor="logError" className="ml-2 text-sm text-gray-700">
          Log error details
        </label>
      </div>
    </div>
  )
}

// Retry Node Configuration
function RetryConfig({
  data,
  onChange,
}: {
  data: Record<string, any>
  onChange: (data: Record<string, any>) => void
}) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Max Retry Attempts
        </label>
        <input
          type="number"
          value={data.maxAttempts || 3}
          onChange={(e) => onChange({ ...data, maxAttempts: parseInt(e.target.value) })}
          min="1"
          max="10"
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Retry Strategy
        </label>
        <select
          value={data.retryStrategy || 'exponential'}
          onChange={(e) => onChange({ ...data, retryStrategy: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
        >
          <option value="exponential">Exponential Backoff</option>
          <option value="linear">Linear Backoff</option>
          <option value="fixed">Fixed Delay</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Base Delay (ms)
        </label>
        <input
          type="number"
          value={data.baseDelay || 1000}
          onChange={(e) => onChange({ ...data, baseDelay: parseInt(e.target.value) })}
          min="100"
          step="100"
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
        />
      </div>

      {data.retryStrategy === 'exponential' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Max Delay (ms)
          </label>
          <input
            type="number"
            value={data.maxDelay || 30000}
            onChange={(e) => onChange({ ...data, maxDelay: parseInt(e.target.value) })}
            min="1000"
            step="1000"
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Retry On (Error Types)
        </label>
        <div className="space-y-2">
          {['timeout', 'rate_limit', 'server_error', 'network_error'].map((type) => (
            <label key={type} className="flex items-center">
              <input
                type="checkbox"
                checked={(data.retryOn || ['timeout', 'rate_limit']).includes(type)}
                onChange={(e) => {
                  const types = data.retryOn || ['timeout', 'rate_limit']
                  const newTypes = e.target.checked
                    ? [...types, type]
                    : types.filter((t: string) => t !== type)
                  onChange({ ...data, retryOn: newTypes })
                }}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">
                {type.replace('_', ' ')}
              </span>
            </label>
          ))}
        </div>
      </div>
    </div>
  )
}

// Default configs for other node types
function LLMConfig({ data, onChange }: { data: Record<string, any>; onChange: (data: Record<string, any>) => void }) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
        <select
          value={data.model || 'gpt-4'}
          onChange={(e) => onChange({ ...data, model: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
        >
          <option value="gpt-4">GPT-4</option>
          <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
          <option value="claude-3-opus">Claude 3 Opus</option>
          <option value="claude-3-sonnet">Claude 3 Sonnet</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Prompt</label>
        <textarea
          value={data.prompt || ''}
          onChange={(e) => onChange({ ...data, prompt: e.target.value })}
          rows={5}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
          placeholder="Enter prompt template..."
        />
      </div>
    </div>
  )
}

function ToolConfig({ data, onChange }: { data: Record<string, any>; onChange: (data: Record<string, any>) => void }) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Tool Name</label>
        <input
          type="text"
          value={data.toolName || ''}
          onChange={(e) => onChange({ ...data, toolName: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          value={data.description || ''}
          onChange={(e) => onChange({ ...data, description: e.target.value })}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
        />
      </div>
    </div>
  )
}

function AgentConfig({ data, onChange }: { data: Record<string, any>; onChange: (data: Record<string, any>) => void }) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Agent ID</label>
        <input
          type="text"
          value={data.agentId || ''}
          onChange={(e) => onChange({ ...data, agentId: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
        />
      </div>
    </div>
  )
}

function HumanInputConfig({ data, onChange }: { data: Record<string, any>; onChange: (data: Record<string, any>) => void }) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Prompt</label>
        <textarea
          value={data.prompt || ''}
          onChange={(e) => onChange({ ...data, prompt: e.target.value })}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
          placeholder="What input do you need from the user?"
        />
      </div>
    </div>
  )
}

function DefaultConfig() {
  return (
    <div>
      <p className="text-sm text-gray-500">No configuration available for this node type.</p>
    </div>
  )
}
